import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import i18next from "i18next";
import "dayjs/locale/fr";
import dayjs from "dayjs";
import { TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ActionsPicker, guidGenerator, useViewport } from "../../../helpers";
import MyMapContainer from "../../../components/geoloc/MyMapContainer";
import SecurityClassManager from "../classes/SecurityClassManager";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { enqueueSnackbar } from "notistack";
import PurchaseGeolocMap from "../../../components/purchaseGeoloc/PurchaseGeolocMap";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MyLocationIcon from "@mui/icons-material/MyLocation";

const PREFIX = "ExparienceSecurity";

const classes = {
  root: `${PREFIX}-root`,
  marginBottom: `${PREFIX}-marginBottom`,
  negateMarginTop: `${PREFIX}-negateMarginTop`,
  maxWidthInput: `${PREFIX}-maxWidthInput`,
  inputsContainer: `${PREFIX}-inputsContainer`,
  marginBottomStyle: `${PREFIX}-marginBottomStyle`,
  subText: `${PREFIX}-subText`,
  datepickerContainer: `${PREFIX}-datepickerContainer`,
  mainTitle: `${PREFIX}-mainTitle`,
  purchaseGeolocMapContainer: `${PREFIX}-purchaseGeolocMapContainer`,
  purchaseGeolocListContainer: `${PREFIX}-purchaseGeolocListContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    marginTop: "0.5rem",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {},
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.negateMarginTop}`]: {
    marginTop: "-0.5rem",
  },

  [`& .${classes.maxWidthInput}`]: {
    maxWidth: "650px",
  },

  [`& .${classes.inputsContainer}`]: {
    marginTop: "1rem",
    maxWidth: "650px",
    width: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.marginBottomStyle}`]: {
    marginBottom: "2rem !important",
  },

  [`& .${classes.subText}`]: {
    marginBottom: "2rem !important",
    textAlign: "center",
  },

  [`& .${classes.datepickerContainer}`]: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      justifyContent: "space-around",
    },
  },

  [`& .${classes.mainTitle}`]: {
    [theme.breakpoints.up("sm")]: {
      textAlign: "center",
    },
    marginBottom: "1rem",
  },

  [`& .${classes.purchaseGeolocMapContainer}`]: {
    position: "relative",
    left: "50%",
    transform: "translate(-50%)",
    width: "100vw",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    maxWidth: "1400px",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
}));

const ExparienceSecurity = (props) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const [positions, setPositions] = useState(null);
  const [disponibility, setDisponibility] = useState(false);
  const [buyGeoloc, setBuyGeoloc] = useState(false);
  const dispoRef = useRef(false);
  const mapRef = useRef(null);

  useEffect(() => {
    if (props.exparience && props.exparience?.startDate) {
      setDisponibility(true);
    }
    if (props.exparience && props.exparience?.priceGeoloc > 0) {
      setBuyGeoloc(true);
    }
  }, [props.exparience]);

  useEffect(() => {
    if (dispoRef.current && !disponibility) {
      props.setExparience({
        ...props.exparience,
        startDate: null,
        endDate: null,
      });
    }
    dispoRef.current = disponibility;
  }, [disponibility]);

  useEffect(() => {
    if (props.exparience?.positions && props.exparience?.positions.length > 0) {
      let tmpPositions = [];
      props.exparience?.positions.forEach((item) => {
        tmpPositions.push({
          lat: item.position.coordinates[0].toString(),
          long: item.position.coordinates[1].toString(),
          guid: guidGenerator(true),
          title: item.position.title,
          radius: item.position.radius,
          visible: item.position.visible,
          free: item.position.free,
          controlDate:
            !item.position.hasOwnProperty("controlDate") &&
            item.position.startDate
              ? true
              : item.position.controlDate
              ? item.position.controlDate
              : false,
          startDate: item.position.startDate || null,
          endDate: item.position.endDate || null,
        });
      });
      // console.log("tmpPositions", tmpPositions);
      setPositions(tmpPositions);
    }
  }, [props.exparience?.positions]);

  function formatGeoJson(pos) {
    const tmp = [...pos];
    const geoPoints = [];
    tmp.forEach((item) => {
      geoPoints.push({
        position: {
          type: "Point",
          coordinates: [parseFloat(item.lat), parseFloat(item.long)],
          title: item.title,
          radius: item.radius,
          visible: item.visible,
          free: item.free,
          controlDate: item.controlDate,
          startDate: item.startDate,
          endDate: item.endDate,
        },
      });
    });
    return geoPoints;
  }

  const handleSavePositions = (positions) => {
    setPositions(positions);
    const newPos = formatGeoJson(positions);
    props.setExparience({ ...props.exparience, positions: newPos });
  };

  const handlePositionFocus = (index) => {
    const item = props.exparience?.purchasedGeolocs[index];
    if (mapRef.current && item) {
      mapRef.current.flyTo(
        item.position.coordinates[0],
        item.position.coordinates[1]
      );
    }
  };

  return (
    <Root>
      <Box className={classes.root}>
        <Box className={classes.inputsContainer}>
          <Typography
            variant="h5"
            className={clsx(classes.mainTitle, classes.marginBottom)}
          >
            {t("exparienceCreateForm.securityTitle")}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) => setDisponibility(e.target.checked)}
                checked={disponibility || false}
              />
            }
            label={
              <Typography variant="body1">
                {t("myExpariences.form.disponibilityTitle")}
              </Typography>
            }
            className={clsx(classes.marginBottom)}
          />
          {disponibility && (
            <>
              <Typography className={classes.subText} variant="body1">
                {t("myExpariences.form.disponibilitySubText")}
              </Typography>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={i18next.language.substring(0, 2)}
              >
                <Box className={classes.datepickerContainer}>
                  <DatePicker
                    disablePast
                    label={t("myExpariences.form.startDate")}
                    slots={{
                      actionBar: ActionsPicker,
                    }}
                    slotProps={{
                      actionBar: {
                        actions: ["clear", "cancel", "accept"],
                      },
                    }}
                    className={classes.marginBottomStyle}
                    onChange={(e) => {
                      props.setExparience({
                        ...props.exparience,
                        startDate: e ? dayjs(e).format("YYYY-MM-DD") : null,
                      });
                    }}
                    onError={(err) =>
                      err
                        ? props.setDatesDisable(true)
                        : props.setDatesDisable(false)
                    }
                    value={
                      props.exparience.startDate
                        ? dayjs(props.exparience?.startDate)
                        : null
                    }
                  />
                  <DatePicker
                    disablePast
                    minDate={
                      props.exparience.startDate
                        ? dayjs(props.exparience?.startDate).add(1, "day")
                        : null
                    }
                    label={t("myExpariences.form.endDate")}
                    slots={{
                      actionBar: ActionsPicker,
                    }}
                    slotProps={{
                      actionBar: {
                        actions: ["clear", "cancel", "accept"],
                      },
                    }}
                    className={classes.marginBottomStyle}
                    onChange={(e) => {
                      props.setExparience({
                        ...props.exparience,
                        endDate: e ? dayjs(e).format("YYYY-MM-DD") : null,
                      });
                    }}
                    onError={(err) =>
                      err
                        ? props.setDatesDisable(true)
                        : props.setDatesDisable(false)
                    }
                    value={
                      props.exparience.endDate
                        ? dayjs(props.exparience.endDate)
                        : null
                    }
                  />
                </Box>
              </LocalizationProvider>
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) =>
                  props.setExparience({
                    ...props.exparience,
                    securedByPassword: e.target.checked,
                  })
                }
                checked={props.exparience.securedByPassword || false}
              />
            }
            label={
              <Typography variant="body1">
                {t("myExpariences.form.securedByPassword")}
              </Typography>
            }
            className={clsx(classes.marginBottom)}
          />
          {props.exparience.securedByPassword && (
            <React.Fragment>
              <TextField
                label={t("myExpariences.form.password")}
                className={clsx(classes.marginBottom, classes.maxWidthInput)}
                value={props.exparience.password}
                error={
                  props.exparience.securedByPassword &&
                  props.exparience.password?.length === 0
                }
                onChange={(e) =>
                  props.setExparience({
                    ...props.exparience,
                    password: e.target.value,
                  })
                }
                fullWidth
                variant={"outlined"}
              />
              {props.exparience.securedByPassword &&
                props.exparience.password?.length === 0 && (
                  <FormHelperText
                    id="password"
                    error
                    className={clsx(
                      classes.marginBottom,
                      classes.negateMarginTop
                    )}
                  >
                    {t("myExpariences.formErrors.passwordTicked")}
                  </FormHelperText>
                )}
            </React.Fragment>
          )}
          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) => {
                  setBuyGeoloc(!buyGeoloc);
                  props.setExparience({
                    ...props.exparience,
                    priceGeoloc: 0,
                  });
                }}
                checked={buyGeoloc}
              />
            }
            label={
              <Typography variant="body1">
                {t("myExpariences.form.userCanBuyGeoloc")}
              </Typography>
            }
            className={clsx(classes.marginBottom)}
          />
          {buyGeoloc && (
            <>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  label={`${t("myExpariences.form.dailyPrice")} (€)`}
                  type="number"
                  value={props.exparience.priceGeoloc}
                  onChange={(e) => {
                    props.setExparience({
                      ...props.exparience,
                      priceGeoloc: Number(e.target.value),
                    });
                  }}
                  variant={"outlined"}
                  className={clsx(classes.marginBottom)}
                  sx={{ width: "50%", marginRight: "10px" }}
                />
                <Tooltip
                  title={t("myExpariences.form.purchaseGeolocLink")}
                  placement="bottom"
                >
                  <IconButton
                    color="primary"
                    sx={{ position: "relative", bottom: "10px" }}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${process.env.REACT_APP_ADMIN_URL}/purchase-geoloc/${props.exparience.id}/`
                      );
                      enqueueSnackbar(t("toasts.copiedInClipboard"), {
                        variant: "info",
                      });
                    }}
                  >
                    <InsertLinkIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={t("myExpariences.form.openPurchaseGeolocLink")}
                  placement="bottom"
                >
                  <IconButton
                    color="primary"
                    sx={{ position: "relative", bottom: "10px" }}
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_ADMIN_URL}/purchase-geoloc/${props.exparience.id}/`,
                        "_blank"
                      );
                    }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}

          {props.exparience.purchasedGeolocs.length > 0 && (
            <Box className={classes.purchaseGeolocMapContainer}>
              <PurchaseGeolocMap
                ref={mapRef}
                displayOnly
                exparienceID={props.exparience.id}
                priceGeoloc={props.exparience.priceGeoloc}
                initialViewState={{
                  latitude:
                    props.exparience.purchasedGeolocs[0].position
                      .coordinates[1],
                  longitude:
                    props.exparience.purchasedGeolocs[0].position
                      .coordinates[0],
                  zoom: 12,
                }}
                purchasedGeolocs={props.exparience.purchasedGeolocs}
                mapID="purchasedGeolocMap"
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) => {
                  props.setExparience({
                    ...props.exparience,
                    securedByPosition: e.target.checked,
                  });
                }}
                checked={props.exparience.securedByPosition || false}
              />
            }
            label={
              <Typography variant="body1">
                {t("myExpariences.form.geolocalisation")}
              </Typography>
            }
            // className={clsx(classes.marginBottom)}
            sx={{
              marginBottom:
                width < 769 && !props.exparience.securedByPosition
                  ? "75px"
                  : "10px",
            }}
          />
          {(!props.exparience.positions ||
            (props.exparience.securedByPosition &&
              props.exparience.positions.length === 0)) && (
            <FormHelperText
              id="geolocalisation"
              error
              className={clsx(classes.marginBottom, classes.negateMarginTop)}
            >
              {t("myExpariences.formErrors.geolocTicked")}
            </FormHelperText>
          )}
        </Box>
        {props.exparience.securedByPosition && (
          // <Box>
          //   <MyMapContainer
          //     positions={positions}
          //     save={handleSavePositions}
          //     setDatesDisable={props.setDatesDisable}
          //   />
          // </Box>
          <Box
            className={classes.purchaseGeolocMapContainer}
            sx={{ marginBottom: width < 769 ? "70px" : "0px" }}
          >
            <PurchaseGeolocMap
              ref={mapRef}
              geolocOnly
              exparienceID={props.exparience.id}
              initialViewState={{
                latitude:
                  props.exparience.purchasedGeolocs[0]?.position
                    .coordinates[1] || 48.866667,
                longitude:
                  props.exparience.purchasedGeolocs[0]?.position
                    .coordinates[0] || 2.333333,
                zoom: 12,
              }}
              geolocs={positions}
              mapID="geolocMap"
              save={handleSavePositions}
            />
          </Box>
        )}
      </Box>
    </Root>
  );
};

export default ExparienceSecurity;
